/**
 * Compliance reports — quarterly access review and oversight packet extracts.
 * super_admin only. Does not enter a tenant.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icons } from '../common/Icons';
import { systemService } from '../../services/api';
import { formatDate, formatDateTime } from '../../utils/formatters';

function formatDue(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = String(isoDate).slice(0, 10).split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

const ACTION_LABELS = {
  account_created: 'Created',
  account_deactivated: 'Deactivated',
  account_reactivated: 'Reactivated',
  account_deleted: 'Deleted',
  role_changed: 'Role changed',
  password_changed: 'Password changed',
  password_reset_by_admin: 'Password reset',
  '2fa_enabled': 'MFA on',
  '2fa_disabled': 'MFA off',
};

function flagBadge(row) {
  if (row.decryptFailed) return <span className="badge badge-warning">Name unread</span>;
  if (row.privilegedMissingMfa) return <span className="badge badge-error">MFA off</span>;
  if (row.stale) return <span className="badge badge-warning">Stale</span>;
  return null;
}

const AccountTable = ({ rows }) => {
  if (!rows.length) {
    return <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>No rows.</p>;
  }
  return (
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            <th>Tenant</th>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Active</th>
            <th>MFA</th>
            <th>Last login</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.section}-${row.tenantSlug}-${row.email}-${row.createdAt}`}>
              <td>{row.tenantName}</td>
              <td>{row.name || '—'}</td>
              <td>{row.email || '—'}</td>
              <td>{row.role || '—'}</td>
              <td>{row.active ? 'Yes' : 'No'}</td>
              <td>{row.mfaEnabled ? 'Yes' : 'No'}</td>
              <td>{formatDateTime(row.lastLogin)}</td>
              <td>{formatDate(row.createdAt)}</td>
              <td>{flagBadge(row)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ComplianceReportsView = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState(null);
  const [changes, setChanges] = useState(null);
  const [acks, setAcks] = useState(null);
  const [incidents, setIncidents] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [userRes, changeRes, ackRes, incidentRes, scoreRes] = await Promise.allSettled([
        systemService.getComplianceReport('user-accounts'),
        systemService.getComplianceReport('access-changes'),
        systemService.getComplianceReport('acknowledgments'),
        systemService.getComplianceReport('incidents'),
        systemService.getGithubScorecard(),
      ]);
      const take = (result) => (result.status === 'fulfilled' ? (result.value?.data || null) : null);
      const failed = [userRes, changeRes, ackRes, incidentRes].filter((result) => result.status === 'rejected');
      setUsers(take(userRes));
      setChanges(take(changeRes));
      setAcks(take(ackRes));
      setIncidents(take(incidentRes));
      setScorecard(scoreRes.status === 'fulfilled' ? (scoreRes.value?.data || null) : null);
      if (failed.length) {
        setError(failed[0].reason?.response?.data?.message || 'Part of the compliance reports failed to load.');
      }
    } catch (err) {
      console.error('Failed to load compliance reports', err);
      setError(err?.response?.data?.message || 'Failed to load compliance reports.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (loading) return;
    const id = window.location.hash.replace('#', '');
    if (!id) return;
    document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [loading]);

  const download = async (name, fallback) => {
    setDownloading(name);
    try {
      await systemService.downloadComplianceReport(name, fallback);
    } catch (err) {
      console.error('Download failed', err);
      setError('Download failed. Try again.');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="empty-state">
        <Icons.Loader size={48} className="spin" />
        <p>Loading compliance reports…</p>
        <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const period = users?.period;
  const issueSummary = scorecard?.issues?.summary;
  const prSummary = scorecard?.pullRequests?.summary;

  return (
    <div>
      {error && (
        <div className="alert alert-warning mb-3">
          <Icons.AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      <div className="card mb-3" style={{ padding: '16px' }}>
        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>
          Quarterly access review and the oversight packet. These queries stay on the
          super-admin path and do not ask a tenant for platform support.
          {period ? ` Current quarter is ${period.label}, due ${formatDue(period.due)}.` : ''}
        </p>
      </div>

      <section id="user-accounts" className="card mb-3">
        <div className="card-header">
          <div>
            <h3 className="card-title">User accounts</h3>
            <p className="card-subtitle">
              {users?.summary?.tenantAccounts ?? 0} tenant accounts
              {' · '}
              {users?.summary?.platformUsers ?? 0} accountant-portal users
              {' · '}
              {users?.summary?.privilegedMissingMfa ?? 0} privileged without MFA
              {' · '}
              {users?.summary?.staleActive ?? 0} active with no login in 90 days
              {(users?.summary?.decryptFailed || 0) > 0
                ? ` · ${users.summary.decryptFailed} names could not be decrypted`
                : ''}
            </p>
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => download('user-accounts', 'user-accounts.csv')}
            disabled={downloading === 'user-accounts'}
          >
            Download CSV
          </button>
        </div>
        <h4 style={{ fontSize: '14px', margin: '0 0 8px' }}>Tenant accounts</h4>
        <AccountTable rows={users?.tenantRows || []} />
        <h4 style={{ fontSize: '14px', margin: '16px 0 8px' }}>Accountant portal</h4>
        <AccountTable rows={users?.platformRows || []} />
      </section>

      <section id="access-changes" className="card mb-3">
        <div className="card-header">
          <div>
            <h3 className="card-title">Access changes</h3>
            <p className="card-subtitle">
              {changes?.summary?.total ?? 0} events in {period?.label || 'this quarter'}
              {changes?.capped ? ' (list capped)' : ''}
            </p>
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => download('access-changes', 'access-changes.csv')}
            disabled={downloading === 'access-changes'}
          >
            Download CSV
          </button>
        </div>
        {(changes?.rows || []).length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>
            No creates, role changes, deactivations, password resets, or MFA changes in this quarter.
          </p>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Action</th>
                  <th>Tenant</th>
                  <th>Target</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {changes.rows.map((row, i) => (
                  <tr key={`${row.createdAt}-${row.action}-${i}`}>
                    <td>{formatDateTime(row.createdAt)}</td>
                    <td>{ACTION_LABELS[row.action] || row.action}</td>
                    <td>{row.tenantName || '—'}</td>
                    <td>{row.targetEmail || row.targetName || '—'}</td>
                    <td>{row.performedByEmail || row.performedByName || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section id="acknowledgments" className="card mb-3">
        <div className="card-header">
          <div>
            <h3 className="card-title">Policy acknowledgments</h3>
            <p className="card-subtitle">
              {acks?.summary?.ownerMissingAck ?? 0} current owner versions with no acknowledgment
              {' · '}
              {acks?.summary?.externalOutstandingPairs ?? 0} outstanding accountant acknowledgments
            </p>
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => download('acknowledgments', 'policy-acknowledgments.csv')}
            disabled={downloading === 'acknowledgments'}
          >
            Download CSV
          </button>
        </div>
        <h4 style={{ fontSize: '14px', margin: '0 0 8px' }}>Owner library</h4>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Version</th>
                <th>Published</th>
                <th>Acks</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(acks?.ownerRows || []).map((row) => (
                <tr key={row.slug}>
                  <td>{row.title}</td>
                  <td>{row.versionN}</td>
                  <td>{formatDate(row.publishedAt)}</td>
                  <td>{row.ackCount}</td>
                  <td>
                    <span className={row.missing ? 'badge badge-warning' : 'badge badge-success'}>
                      {row.missing ? 'No owner ack' : 'Acked'}
                    </span>
                  </td>
                </tr>
              ))}
              {(acks?.ownerRows || []).length === 0 && (
                <tr>
                  <td colSpan={5} style={{ color: 'var(--text-muted)' }}>No published owner documents.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <h4 style={{ fontSize: '14px', margin: '16px 0 8px' }}>External accountant</h4>
        {(acks?.externalRows || []).length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>
            No published external-accountant document, or no active accountant-portal user.
          </p>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Version</th>
                  <th>Accountant</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {acks.externalRows.map((row) => (
                  <tr key={`${row.slug}-${row.signerEmail}`}>
                    <td>{row.title}</td>
                    <td>{row.versionN}</td>
                    <td>{row.signerName} ({row.signerEmail})</td>
                    <td>
                      <span className={row.acked ? 'badge badge-success' : 'badge badge-warning'}>
                        {row.acked ? 'Acked' : 'Outstanding'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section id="change-scorecard" className="card mb-3">
        <div className="card-header">
          <div>
            <h3 className="card-title">Change scorecard</h3>
            <p className="card-subtitle">
              {scorecard?.configured
                ? `${issueSummary?.fail ?? 0} issue fails · ${prSummary?.fail ?? 0} pull-request fails · ${issueSummary?.warn ?? 0} issue warnings`
                : 'GitHub token is not set on the API. The scorecard page explains how to turn it on.'}
            </p>
          </div>
          <Link className="btn btn-secondary btn-sm" to="/admin/github-scorecard">
            Open scorecard
          </Link>
        </div>
      </section>

      <section id="incidents" className="card mb-3">
        <div className="card-header">
          <div>
            <h3 className="card-title">Incidents</h3>
            <p className="card-subtitle">
              {incidents?.configured
                ? `${incidents.summary?.open ?? 0} open Sev1/Sev2 · ${incidents.summary?.openedInPeriod ?? 0} opened this quarter · ${incidents.summary?.closedInPeriod ?? 0} closed this quarter`
                : 'Same GitHub token as the change scorecard. Without it this extract is empty.'}
              {incidents?.capped ? ' Search results were capped.' : ''}
            </p>
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => download('incidents', 'incidents.csv')}
            disabled={downloading === 'incidents' || !incidents?.configured}
          >
            Download CSV
          </button>
        </div>
        {(incidents?.rows || []).length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>
            {incidents?.configured
              ? 'No open Sev1/Sev2 issues, and none opened or closed this quarter.'
              : 'Not loaded.'}
          </p>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Severity</th>
                  <th>Title</th>
                  <th>State</th>
                  <th>Grade</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {incidents.rows.map((row) => (
                  <tr key={row.number}>
                    <td>
                      <a href={row.htmlUrl} target="_blank" rel="noreferrer">{row.number}</a>
                    </td>
                    <td>{row.severity}</td>
                    <td>{row.title}</td>
                    <td>{row.state}</td>
                    <td>
                      <span className={row.grade === 'fail' ? 'badge badge-error' : row.grade === 'warn' ? 'badge badge-warning' : 'badge badge-success'}>
                        {row.grade}
                      </span>
                    </td>
                    <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{(row.notes || []).join(' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default ComplianceReportsView;
